import type { ReactNode } from "react";

import type { CompanyAccountType } from "src/services/analytics.types";

/**
 * Route paths for the report module, mounted under `/reports`.
 * Single source of truth — reuse these instead of hard-coding `/reports...` strings.
 * The child paths (other than `root`) are relative to the `/reports` wrapper route.
 */
export const REPORT_ROUTES = {
  root: "/reports",
  daily: "daily",
  crm: "crm",
  project: "project",
  worker: "worker",
  vendor: "vendor",
  expense: "expense",
  subcontractor: "subcontractor",
  client: "client",
  employee: "employee",
  user: "user",
  material: "material",
  account: "account",
  tool: "tool",
} as const;

export const dailyReportColumnHeaders = [
  {
    field: "sl",
    headerName: "Sl No",
    flex: 1,
  },
  {
    field: "projectName",
    headerName: "Project Name",
    flex: 2,
  },
  {
    field: "entryBy",
    headerName: "entry by",
    flex: 2,
  },
  {
    field: "description",
    headerName: "description",
    flex: 2,
  },
  {
    field: "particulars",
    headerName: "particulars",
    flex: 1.5,
  },
  {
    field: "amount",
    headerName: "amount",
    flex: 1,
  },
  {
    field: "cashIn",
    headerName: "in",
    flex: 1,
  },
  {
    field: "cashOut",
    headerName: "out",
    flex: 1,
  },
  // TODO: will be enabled once the account feature is implemented
  // {
  //   field: "account",
  //   headerName: "account",
  //   flex: 1,
  // },
];

export const crmReportColumnHeaders = [
  {
    field: "date",
    headerName: "Date",
    flex: 1,
  },
  {
    field: "assignee",
    headerName: "Assignee",
    flex: 1,
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
  },
  {
    field: "count",
    headerName: "Count",
    flex: 0.5,
  },
  {
    field: "percentage",
    headerName: "Percentage",
    flex: 0.5,
  },
];

export const projectReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 1, sortable: false },
    {
      field: "projectName",
      headerName: "Project",
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "openingBalance",
      headerName: "Opening Balance",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalOpeningBalance}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalWork",
      headerName: "Total Work",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalWorkAmount}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalPurchase",
      headerName: "Total Purchase",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalPurchaseAmount}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalOtherExpense",
      headerName: "Total Expense",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalExpense}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalIncome",
      headerName: "Total Income",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalIncomeAmount}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "closingBalance",
      headerName: "Closing Balance",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalClosingBalance}</strong>;
        }
        return params.value;
      },
    },
  ],
  detailed: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    { field: "formattedDate", headerName: "Date", flex: 1.3, sortable: false },
    {
      field: "particulars",
      headerName: "Particulars",
      flex: 2,
      sortable: false,
    },
    {
      field: "projectName",
      headerName: "Project",
      flex: 2,
      sortable: false,
    },
    {
      field: "projectType",
      headerName: "Type",
      flex: 1.5,
      sortable: false,
    },
    {
      field: "description",
      headerName: "Remarks",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "income",
      headerName: "Income",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalCashIn}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "expense",
      headerName: "Expense",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalCashOut}</strong>;
        }
        return params.value;
      },
    },
  ],
};

export const workerReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    {
      field: "workerName",
      headerName: "Worker",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "openingBalance",
      headerName: "Old Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalOpeningBalance}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalWage",
      headerName: "Total Worked",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalWorkedWage}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalOvertime",
      headerName: "Overtime",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalOvertime}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalPaid",
      headerName: "Paid",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalPaid}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "closingBalance",
      headerName: "Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalClosingBalance}</strong>;
        }
        return params.value;
      },
    },
  ],
  detailed: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    { field: "formattedDate", headerName: "Date", flex: 1, sortable: false },
    {
      field: "type",
      headerName: "Type",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (params.row.id === "subtotal" ? "" : params.value),
    },
    { field: "workerName", headerName: "Name", flex: 1, sortable: false },
    {
      field: "remark",
      headerName: "Project / Remark",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "wage",
      headerName: "Wage",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalDailyWage}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "overtime",
      headerName: "Overtime",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalDailyOvertime}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "amount",
      headerName: "Paid",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalDailyPaid}</strong>;
        }
        return params.value;
      },
    },
  ],
};

export const vendorReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    {
      field: "vendorName",
      headerName: "Vendor",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "openingBalance",
      headerName: "Old Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalOpeningBalance}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalPurchase",
      headerName: "Total Purchased",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalPurchaseAmount}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalReturn",
      headerName: "Total Return",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalReturnAmount}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalPaid",
      headerName: "Paid",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalPaidAmount}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "closingBalance",
      headerName: "Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalClosingBalance}</strong>;
        }
        return params.value;
      },
    },
  ],
  detailed: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    { field: "formattedDate", headerName: "Date", flex: 1, sortable: false },
    { field: "vendorName", headerName: "Vendor", flex: 1, sortable: false },
    {
      field: "projectName",
      headerName: "Project",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "purchaseAmount",
      headerName: "Purchase",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalPurchase}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "returnAmount",
      headerName: "Return",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalReturn}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "paymentAmount",
      headerName: "Payment",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalPayment}</strong>;
        }
        return params.value;
      },
    },
  ],
};

export const expenseReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    {
      field: "categoryName",
      headerName: "Category",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalAmount}</strong>;
        }
        return params.value;
      },
    },
  ],
  detailed: [
    { field: "formattedDate", headerName: "Date", flex: 1, sortable: false },
    { field: "categoryName", headerName: "Category", flex: 1, sortable: false },
    {
      field: "projectName",
      headerName: "Project",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    { field: "description", headerName: "Remarks", flex: 1.5, sortable: false },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalAmount}</strong>;
        }
        return params.value;
      },
    },
  ],
};

/** Column cell that shows the subtotal row's aggregate (bold) instead of the row value. */
const subtotalCell = (subtotalField: string) => (params: { row: Record<string, unknown>; value?: unknown }) => {
  if (params.row.id === "subtotal") {
    return <strong>{String(params.row[subtotalField] ?? "")}</strong>;
  }
  return params.value as ReactNode;
};

export const subcontractorReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    { field: "subcontractorName", headerName: "Subcontractor", flex: 1, sortable: false, renderCell: subtotalCell("label") },
    {
      field: "openingBalance",
      headerName: "Old Balance",
      flex: 1,
      sortable: false,
      renderCell: subtotalCell("totalOpeningBalance"),
    },
    {
      field: "totalContractValue",
      headerName: "Total Contract",
      flex: 1,
      sortable: false,
      renderCell: subtotalCell("totalContractValueSum"),
    },
    { field: "totalPaid", headerName: "Total Paid", flex: 1, sortable: false, renderCell: subtotalCell("totalPaidSum") },
    { field: "closingBalance", headerName: "Balance", flex: 1, sortable: false, renderCell: subtotalCell("totalClosingBalance") },
  ],
  detailed: [
    { field: "sl", headerName: "Sl No", flex: 0.6, sortable: false },
    { field: "formattedDate", headerName: "Date", flex: 0.9, sortable: false },
    { field: "entryLabel", headerName: "Type", flex: 0.8, sortable: false },
    { field: "subcontractorName", headerName: "Subcontractor", flex: 1, sortable: false },
    { field: "projectName", headerName: "Project", flex: 1, sortable: false },
    { field: "details", headerName: "Description", flex: 1.4, sortable: false, renderCell: subtotalCell("label") },
    { field: "contractAmount", headerName: "Contract", flex: 1, sortable: false, renderCell: subtotalCell("totalContract") },
    { field: "paymentAmount", headerName: "Payment", flex: 1, sortable: false, renderCell: subtotalCell("totalPayment") },
  ],
};

export const clientReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    {
      field: "clientName",
      headerName: "Client",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "openingBalance",
      headerName: "Old Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalOpeningBalance}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalReceivables",
      headerName: "Total Receivable",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalReceivables}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalIncomes",
      headerName: "Total Income",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalIncomes}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "closingBalance",
      headerName: "Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalClosingBalance}</strong>;
        }
        return params.value;
      },
    },
  ],
  detailed: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    { field: "formattedDate", headerName: "Date", flex: 1, sortable: false },
    { field: "clientName", headerName: "Client", flex: 1, sortable: false },
    {
      field: "projectName",
      headerName: "Project",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "payment",
      headerName: "Receivable",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalReceivableAmount}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "income",
      headerName: "Income",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalIncomeAmount}</strong>;
        }
        return params.value;
      },
    },
  ],
};

export const userReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 1, sortable: false },
    {
      field: "userName",
      headerName: "User",
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "openingBalance",
      headerName: "Old Balance",
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalOpeningBalance}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalCredit",
      headerName: "Total In",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalIncome}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalDebit",
      headerName: "Total Out",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalExpense}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "closingBalance",
      headerName: "Balance",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalClosingBalance}</strong>;
        }
        return params.value;
      },
    },
  ],
  detailed: [
    { field: "sl", headerName: "Sl No", flex: 0.8, sortable: false },
    { field: "formattedDate", headerName: "Date", flex: 1, sortable: false },
    {
      field: "fromAccountName",
      headerName: "From",
      flex: 2,
      sortable: false,
    },
    {
      field: "toAccountName",
      headerName: "To",
      flex: 2,
      sortable: false,
    },
    {
      field: "projectName",
      headerName: "Project",
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      sortable: false,
    },
    {
      field: "income",
      headerName: "In",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalIncomeAmount}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "expense",
      headerName: "Out",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalExpenseAmount}</strong>;
        }
        return params.value;
      },
    },
  ],
};

export const materialReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    {
      field: "materialName",
      headerName: "Material",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalQuantity}</strong>;
        }
        return params.value;
      },
    },
    { field: "unit", headerName: "Unit", flex: 0.8, sortable: false },
    {
      field: "totalPurchase",
      header: "Total Purchase",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalPurchaseAmount}</strong>;
        }
        return params.value;
      },
    },
  ],
  detailed: [
    { field: "sl", headerName: "Sl No", flex: 1, sortable: false },
    { field: "formattedDate", headerName: "Date", flex: 1, sortable: false },
    { field: "vendorName", headerName: "Vendor", flex: 1, sortable: false },
    { field: "projectName", headerName: "Project", flex: 1, sortable: false },
    {
      field: "materialName",
      headerName: "Material",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalQuantity}</strong>;
        }
        return params.value;
      },
    },
    { field: "unit", headerName: "Unit", flex: 0.8, sortable: false },
    {
      field: "total",
      headerName: "Total Amount",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalPrice}</strong>;
        }
        return params.value;
      },
    },
  ],
};

export const toolReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 0.7, sortable: false },
    {
      field: "toolName",
      headerName: "Tool",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalIssued",
      headerName: "Total Issued",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalIssuedSum}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "totalReturned",
      headerName: "Total Returned",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.totalReturnedSum}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "netUsed",
      headerName: "Net Used",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.netUsedSum}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "currentBalance",
      headerName: "Current Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.currentBalanceSum}</strong>;
        }
        return params.value;
      },
    },
  ],
  detailed: [
    { field: "sl", headerName: "Sl No", flex: 1, sortable: false },
    { field: "formattedDate", headerName: "Date", flex: 1, sortable: false },
    { field: "toolName", headerName: "Tool", flex: 1, sortable: false },
    {
      field: "projectName",
      headerName: "Project",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.label}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.currentBalanceSum}</strong>;
        }
        return params.value;
      },
    },
    {
      field: "currentBalance",
      headerName: "Current Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.id === "subtotal") {
          return <strong>{params.row.quantitySum}</strong>;
        }
        return params.value;
      },
    },
  ],
};

export const accountReportColumns = {
  summary: [
    { field: "sl", headerName: "Sl No", flex: 0.6, sortable: false },
    { field: "accountName", headerName: "Account", flex: 1.4, sortable: false, renderCell: subtotalCell("label") },
    { field: "accountType", headerName: "Type", flex: 0.8, sortable: false },
    {
      field: "openingBalance",
      headerName: "Old Balance",
      flex: 1,
      sortable: false,
      renderCell: subtotalCell("totalOpeningBalance"),
    },
    { field: "totalCredit", headerName: "Total In", flex: 1, sortable: false, renderCell: subtotalCell("totalCreditSum") },
    { field: "totalDebit", headerName: "Total Out", flex: 1, sortable: false, renderCell: subtotalCell("totalDebitSum") },
    { field: "closingBalance", headerName: "Balance", flex: 1, sortable: false, renderCell: subtotalCell("totalClosingBalance") },
  ],
  detailed: [
    { field: "sl", headerName: "Sl No", flex: 0.6, sortable: false },
    { field: "formattedDate", headerName: "Date", flex: 0.9, sortable: false },
    { field: "accountName", headerName: "Account", flex: 1, sortable: false },
    { field: "counterpartyLabel", headerName: "From / To", flex: 1.2, sortable: false },
    { field: "projectName", headerName: "Project", flex: 1, sortable: false },
    { field: "description", headerName: "Description", flex: 1.2, sortable: false, renderCell: subtotalCell("label") },
    { field: "creditAmount", headerName: "In", flex: 0.9, sortable: false, renderCell: subtotalCell("totalCredit") },
    { field: "debitAmount", headerName: "Out", flex: 0.9, sortable: false, renderCell: subtotalCell("totalDebit") },
  ],
};

/** Company account types accepted by the account report (backend ACCOUNT_REPORT_TYPES — no per-user USER accounts). */
export const ACCOUNT_REPORT_TYPE_OPTIONS: { id: CompanyAccountType; name: string }[] = [
  { id: "BANK", name: "Bank" },
  { id: "CASH", name: "Cash" },
  { id: "LOAN", name: "Loan" },
  { id: "E_WALLET", name: "E-Wallet" },
  { id: "CREDIT_CARD", name: "Credit Card" },
  { id: "DEBIT_CARD", name: "Debit Card" },
  { id: "PREPAID_CARD", name: "Prepaid Card" },
  { id: "OTHER", name: "Other" },
];
